import React, { useEffect, useState } from 'react';
import { Typography, Card, Form, Input, Button, Switch, message, Upload, Space, Image } from 'antd';
import { SaveOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { applySystemSettingsBrand, getSystemSettings, saveSystemSettings } from '../services/systemSettingsService';
import { normalizeFileUrl, uploadFile } from '../services/fileService';
import { useAppDispatch } from '../store';
import { updateSettings } from '../store/slices/settingsSlice';
import type { SystemSettings as SystemSettingsValues } from '../types';

const { Title } = Typography;

const SystemSettings: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const logoValue = Form.useWatch('logo', form);

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const settings = await getSystemSettings();
        form.setFieldsValue(settings);
        dispatch(updateSettings(settings));
        applySystemSettingsBrand(settings);
      } catch {
        message.error('加载系统设置失败');
      } finally {
        setLoading(false);
      }
    };

    void loadSettings();
  }, [dispatch, form]);

  const handleSubmit = async (values: SystemSettingsValues) => {
    setSaving(true);
    try {
      const settings = await saveSystemSettings(values);
      form.setFieldsValue(settings);
      dispatch(updateSettings(settings));
      applySystemSettingsBrand(settings);
      message.success('保存成功');
    } catch {
      message.error('保存系统设置失败');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setLogoUploading(true);
    try {
      const res = await uploadFile(file as File, 'admin_system_logo');
      const url = res.data.url;
      form.setFieldsValue({ logo: url });
      message.success('Logo 上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Logo 上传失败');
      onError?.(error);
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2}>系统设置</Title>
      </div>

      <div className="page-content">
        <Card title="基础设置" loading={loading} style={{ marginBottom: 24 }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              siteName: '悠米宝贝宠物管理平台',
              version: '1.0.0',
            }}
          >
            <Form.Item
              name="siteName"
              label="系统名称"
              rules={[{ required: true, message: '请输入系统名称' }]}
            >
              <Input placeholder="请输入系统名称" />
            </Form.Item>

            <Form.Item name="logo" label="系统Logo">
              <Input.Group compact>
                <Input style={{ width: 'calc(100% - 120px)' }} placeholder="请上传Logo" readOnly />
                <Upload accept="image/*" showUploadList={false} customRequest={handleLogoUpload}>
                  <Button loading={logoUploading} icon={<UploadOutlined />}>
                    上传 Logo
                  </Button>
                </Upload>
              </Input.Group>
            </Form.Item>
            {logoValue ? (
              <Form.Item label="Logo预览">
                <Space>
                  <Image
                    src={normalizeFileUrl(logoValue)}
                    width={64}
                    height={64}
                    style={{ objectFit: 'contain', borderRadius: 8, border: '1px solid #f0f0f0' }}
                  />
                  <Button onClick={() => form.setFieldsValue({ logo: '' })}>清除 Logo</Button>
                </Space>
              </Form.Item>
            ) : null}

            <Form.Item name="version" label="系统版本">
              <Input placeholder="请输入系统版本" />
            </Form.Item>

            <Form.Item name="announcement" label="系统公告">
              <Input.TextArea rows={3} placeholder="请输入系统公告" />
            </Form.Item>

            <Form.Item name="maintenance" label="维护模式" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Card>

        <Card title="操作">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={() => form.submit()}
          >
            保存设置
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default SystemSettings;
